// =============================================================================
// components/KpiIncidents/IncidentDashboard.jsx
//
// Incident dashboard with a linked view between the table and the map.
// Dynamic layout that adapts to any vertical and horizontal resolution.
//
// FIX5 — dropped server-side pagination entirely:
//   - On mount, ONLY lookups load (they feed the filter dropdowns and are
//     cheap/static — independent of Status or of which incidents exist).
//   - Tech/KPI are still pre-filled with a sensible default as soon as
//     lookups arrive (nice starting point), but nothing is fetched yet.
//   - Incidents (and Sites, needed for the map) only load when the operator
//     clicks Apply. The server now returns EVERYTHING matching the filters
//     in one shot (up to a maxRows safety cap — see incidentActions), and
//     the table paginates that already-filtered set in memory. There is no
//     more page-change effect re-fetching from the server.
//   - Real volumes can still exceed maxRows if the filters are left broad
//     (e.g. ~28,900 rows observed for Tech=4G alone in the chronic duration
//     bucket, with no KPI filter). `totalCount` from the server is the TRUE
//     match count even when truncated, so an Alert warns the operator to
//     narrow the filters further (pick a KPI, a tighter duration bucket) if
//     that happened — silently showing partial data would be worse than
//     the old page-by-page bias problem this whole rework was meant to fix.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';

import {
	Box,
	Stack,
	Alert,
	CircularProgress,
	Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import {
	Group,
	Panel,
	Separator,
} from 'react-resizable-panels';

import {
	useIncidents,
	useLookups,
	useSites,
} from '../../hooks/useKpi';

import IncidentFilters     from './IncidentFilters';
import IncidentDetailPanel from './IncidentDetailPanel';
import IncidentTable       from './IncidentTable';
import IncidentMap         from './IncidentMap';

export default function IncidentDashboard() {

	// --------------------------------------------------------------
	// Data hooks
	// --------------------------------------------------------------
	const incidents = useIncidents();
	const lookups   = useLookups();
	const sites     = useSites();

	// Initial load: ONLY lookups (feeds the filter dropdowns). Incidents
	// and Sites are deferred to the first "Apply" click.
	useEffect(function () {
		lookups.load();
	}, []);

	// --------------------------------------------------------------
	// Default Tech + KPI selection — pre-fills the dropdowns as soon as
	// lookups arrive, so the operator isn't starting from a blank "All /
	// All". Does NOT trigger a load; that still requires Apply.
	// --------------------------------------------------------------
	const didAutoSelectDefaults = useRef(false);

	useEffect(function () {
		if (didAutoSelectDefaults.current) return;
		if (!lookups.techs || lookups.techs.length === 0) return;
		if (!lookups.kpis || lookups.kpis.length === 0) return;

		didAutoSelectDefaults.current = true;

		if (incidents.filters.tech !== 'all' || incidents.filters.kpiName !== 'all') {
			return; // already set some other way — don't override
		}

		const techs = lookups.techs.slice().sort();
		const firstTech = techs[0];
		if (!firstTech) return;

		const kpisForTech = lookups.kpis
			.filter(function (k) { return k.tech === firstTech; })
			.map(function (k) { return k.kpiName; })
			.filter(Boolean)
			.sort();
		const firstKpi = kpisForTech.length > 0 ? kpisForTech[0] : 'all';

		incidents.updateFilters({ tech: firstTech, kpiName: firstKpi });
	}, [lookups.techs, lookups.kpis]);

	// --------------------------------------------------------------
	// Apply
	// --------------------------------------------------------------
	const [hasApplied, setHasApplied] = useState(false);
	const sitesLoadedOnce = useRef(false);

	const handleApply = function () {
		setHasApplied(true);
		incidents.load();
		if (!sitesLoadedOnce.current) {
			sitesLoadedOnce.current = true;
			sites.load();
		}
	};

	// --------------------------------------------------------------
	// Local (UI) state
	// --------------------------------------------------------------
	const [selectedSiteId, setSelectedSiteId] = useState(null);

	// --------------------------------------------------------------
	// Derived values
	// --------------------------------------------------------------
	const visibleItems = useMemo(function () {
		if (!selectedSiteId) return incidents.items;
		return incidents.items.filter(function (item) {
			return item.siteId === selectedSiteId;
		});
	}, [incidents.items, selectedSiteId]);

	const flyToCoords = useMemo(function () {
		if (!incidents.selected) return null;
		const key = incidents.selected.vendor + '|' + incidents.selected.siteId;
		const site = sites.siteMap.get(key);
		if (!site) return null;
		return {
			lat:  site.latitude,
			lng:  site.longitude,
			zoom: 17,
		};
	}, [incidents.selected, sites.siteMap]);

	// True only when the server had MORE rows matching the filters than it
	// actually returned (maxRows safety cap kicked in).
	const isTruncated = hasApplied
		&& incidents.totalCount > 0
		&& incidents.totalCount > incidents.items.length;

	// --------------------------------------------------------------
	// Handlers
	// --------------------------------------------------------------
	const handleSiteClick = function (site) {
		setSelectedSiteId(function (prev) {
			return prev === site.siteId ? null : site.siteId;
		});
	};

	const handleClearSelection = function () {
		setSelectedSiteId(null);
		if (incidents.select) incidents.select(null);
	};

	// --------------------------------------------------------------
	// Error state
	// --------------------------------------------------------------
	if (incidents.error) {
		return <Alert severity="error">{incidents.error}</Alert>;
	}

	// --------------------------------------------------------------
	// Render
	// --------------------------------------------------------------
	return (
		<Stack
			spacing={2}
			sx={{
				p: 2,
				height: 'calc(100vh - 70px)', // Fills all the available height for the resolution
				minHeight: 550,
				boxSizing: 'border-box'
			}}
		>

			<IncidentFilters
				filters={incidents.filters}
				lookups={lookups}
				onChange={incidents.updateFilters}
				onReset={incidents.clearFilters}
				onApply={handleApply}
				loading={incidents.loading}
			/>

			{isTruncated && (
				<Alert severity="warning" sx={{ py: 0.25 }}>
					Showing {incidents.items.length.toLocaleString()} of {incidents.totalCount.toLocaleString()} matching
					incidents — narrow the filters (e.g. pick a specific KPI or a tighter duration range) to see the rest.
				</Alert>
			)}

			{incidents.selected && (
				<IncidentDetailPanel
					incident={incidents.selected}
					history={incidents.history}
					loading={incidents.historyLoading}
					lookups={lookups}
					onClose={function () { incidents.select(null); }}
				/>
			)}

			{!hasApplied ? (
				<Box sx={{
					flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
					alignItems: 'center', justifyContent: 'center', gap: 1.5,
					color: 'text.secondary', border: '1px dashed', borderColor: 'divider',
					borderRadius: 1,
				}}>
					<FilterAltIcon sx={{ fontSize: 32, opacity: 0.4 }} />
					<Typography variant="body2">
						Choose your filters above, then click <strong>Apply</strong> to load incidents.
					</Typography>
				</Box>
			) : (
				/* Responsive horizontal split: flex: 1 keeps it fitting the resolution */
				<Box sx={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}>
					{incidents.loading && (
						<Box sx={{
							position: 'absolute', inset: 0, zIndex: 1,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							bgcolor: 'rgba(255,255,255,0.6)',
						}}>
							<CircularProgress />
						</Box>
					)}
					<Group
						orientation="horizontal"
						defaultLayout={{ 'table-panel': 75, 'map-panel': 25 }} // Wider table so all columns are visible
						style={{ height: '100%', width: '100%' }}
					>

						{/* Table */}
						<Panel id="table-panel" minSize="40%">
							<Box sx={{ height: '100%', pr: 0.5, overflow: 'hidden' }}>
								<IncidentTable
									rows={visibleItems}
									loading={incidents.loading}
									onRowClick={incidents.select}
									selected={incidents.selected}
									lookups={lookups}
								/>
							</Box>
						</Panel>

						{/* Resizable separator */}
						<Separator
							style={{
								flexShrink: 0,
								width: 6,
								background: '#e5e7eb',
								cursor: 'col-resize',
								transition: 'background-color 0.15s',
							}}
						/>

						{/* Map */}
						<Panel id="map-panel" minSize="25%">
							<Box sx={{ height: '100%', pl: 0.5, overflow: 'hidden' }}>
								<IncidentMap
									items={incidents.items}
									siteMap={sites.siteMap}
									severityMap={lookups.severityMap}
									statusMap={lookups.statusMap}
									selectedSiteId={selectedSiteId}
									selectedIncident={incidents.selected}
									flyToCoords={flyToCoords}
									onSiteClick={handleSiteClick}
									onClearSelection={handleClearSelection}
									onIncidentSelect={incidents.select}
								/>
							</Box>
						</Panel>

					</Group>
				</Box>
			)}

		</Stack>
	);
}